import { useState } from 'react';
import { useServiceWorker } from '../../context/ServiceWorkerContext';
import { useOffline } from '../../hooks/useOffline';

/**
 * Prompts the user to move onto a downloaded update, from anywhere in the app.
 *
 * Rendered once at the root. An iOS home-screen PWA has no reload button, so
 * without this the only way onto a new version is force-quitting the app.
 */
export function UpdateBanner() {
  const { needRefresh, applyUpdate } = useServiceWorker();
  const isOffline = useOffline();
  const [applying, setApplying] = useState(false);

  // Applying an update reloads the page, which offline would land on whatever the
  // cache holds. Wait until the connection is back.
  if (!needRefresh || isOffline) return null;

  const handleApply = () => {
    setApplying(true);
    // Reloads on success; on failure clear the spinner so the user can retry.
    void applyUpdate().catch(() => setApplying(false));
  };

  return (
    <div
      className="fixed inset-x-0 bottom-0 z-50 border-t border-primary/20 bg-primary-light px-4 py-3 shadow-elevated safe-bottom"
      role="status"
      aria-live="polite"
    >
      <div className="mx-auto flex max-w-md items-center justify-between gap-3">
        <p className="text-sm font-medium text-text-primary">
          A new version of Chloropal is ready.
        </p>
        <button
          type="button"
          onClick={handleApply}
          disabled={applying}
          className="min-h-[44px] shrink-0 rounded-lg bg-primary px-4 py-2 text-sm font-semibold text-white transition-opacity disabled:opacity-50"
          aria-label="Update to the new version now"
        >
          {applying ? 'Updating…' : 'Update'}
        </button>
      </div>
    </div>
  );
}
